import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Calendar, Download, Eye, Search, RefreshCw, MessageSquare, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import FolderTree from './FolderTree';
import FolderComments from './FolderComments';
import Breadcrumb from './Breadcrumb';

const Dashboard = () => {
  const { user } = useAuth();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'list'
  const [currentPath, setCurrentPath] = useState(''); // For breadcrumb navigation

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setError('');
      const response = await api.get('/pdf/list');
      setTree(response.data.tree);
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Hiba történt a fájlok betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchFiles();
    setRefreshing(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Flatten tree structure for search
  const flattenTree = (nodes) => {
    let result = [];
    for (const node of nodes) {
      if (node.type === 'file') {
        result.push(node);
      }
      if (node.children && node.children.length > 0) {
        result = result.concat(flattenTree(node.children));
      }
    }
    return result;
  };

  // Filter tree based on search term
  const filterTree = (nodes, searchTerm) => {
    if (!searchTerm) return nodes;
    
    return nodes.reduce((filtered, node) => {
      const matchesSearch = node.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (node.type === 'file' && matchesSearch) {
        filtered.push(node);
      } else if (node.type === 'folder') {
        const filteredChildren = filterTree(node.children || [], searchTerm);
        if (filteredChildren.length > 0 || matchesSearch) {
          filtered.push({
            ...node,
            children: filteredChildren
          });
        }
      }
      
      return filtered;
    }, []);
  };

  const filteredTree = filterTree(tree, searchTerm);
  const allFiles = flattenTree(tree);
  const filteredFiles = searchTerm ? flattenTree(filteredTree) : allFiles;

  const handleFolderSelect = (folder) => {
    setSelectedFolder(selectedFolder === folder.path ? null : folder.path);
  };

  const getSelectedFolderData = () => {
    if (!selectedFolder) return null;
    
    const findFolderInTree = (nodes, path) => {
      for (const node of nodes) {
        if (node.type === 'folder' && node.path === path) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findFolderInTree(node.children, path);
          if (found) return found;
        }
      }
      return null;
    };
    
    const folderData = findFolderInTree(tree, selectedFolder);
    if (folderData) {
      // Add display path for better UI
      folderData.displayPath = selectedFolder.replace(/\//g, ' → ') || 'Gyökér könyvtár';
    }
    return folderData;
  };

  const selectedFolderData = getSelectedFolderData();

  const handleBreadcrumbNavigate = (path) => {
    setCurrentPath(path);
    setSelectedFolder(null);
    // If needed, we could also filter the tree to show only contents of this path
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Fájlok betöltése...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">PDF Dokumentumok</h1>
        <p className="text-gray-600">Elérhető dokumentumok megtekintése és megjegyzések kezelése</p>
      </div>

      {/* Search, View Mode and Refresh Controls */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Fájl vagy mappa keresése..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('tree')}
              className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'tree'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Fa nézet
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                viewMode === 'list'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Lista nézet
            </button>
          </div>
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-secondary flex items-center space-x-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Frissítés</span>
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* File Count */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {viewMode === 'tree' ? (
            <>
              {tree.length} elem található
              {searchTerm && ` a "${searchTerm}" keresésre`}
            </>
          ) : (
            <>
              {filteredFiles.length} fájl található
              {searchTerm && ` a "${searchTerm}" keresésre`}
            </>
          )}
        </div>
        
        {selectedFolder && (
          <div className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
            Kiválasztva: {selectedFolder}
          </div>
        )}
      </div>

      {/* Breadcrumb Navigation */}
      {currentPath && (
        <div className="mb-4">
          <Breadcrumb 
            path={currentPath} 
            onNavigate={handleBreadcrumbNavigate}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex gap-6">
        {/* Left Column - Files and Folders */}
        <div className={`${selectedFolder ? 'lg:w-2/3' : 'w-full'} transition-all duration-300`}>
          {/* Tree or List View */}
          {viewMode === 'tree' ? (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <FolderTree 
                tree={filteredTree} 
                onFolderSelect={handleFolderSelect}
                selectedFolder={selectedFolder}
                showComments={true}
                onNavigate={handleBreadcrumbNavigate}
              />
            </div>
          ) : (
            /* List View */
            filteredFiles.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? 'Nincs találat' : 'Nincsenek fájlok'}
                </h3>
                <p className="text-gray-600">
                  {searchTerm 
                    ? 'Próbáljon meg más keresési kifejezést használni.' 
                    : 'Jelenleg nincsenek PDF fájlok feltöltve.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredFiles.map((file) => (
                  <div key={file.id} className="card p-6 hover:shadow-lg transition-shadow duration-200">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <FileText className="h-8 w-8 text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                          {file.name}
                        </h3>
                        {file.path !== file.name && (
                          <p className="text-xs text-gray-500 mt-1">
                            📁 {file.path.replace('/' + file.name, '')}
                          </p>
                        )}
                        <div className="mt-2 space-y-1 text-xs text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Download className="h-3 w-3" />
                            <span>{formatFileSize(file.size)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(file.modified)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <Link
                        to={`/pdf/${file.id}`}
                        className="w-full btn-primary flex items-center justify-center space-x-2 text-sm"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Megtekintés</span>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Right Column - Comments Sidebar */}
        {selectedFolder && selectedFolderData && (
          <div className="lg:w-1/3 bg-white rounded-lg border border-gray-200 p-4 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <MessageSquare className="h-5 w-5 text-primary-600" />
                <span>Megjegyzések</span>
              </h3>
              <button
                onClick={() => setSelectedFolder(null)}
                className="text-gray-400 hover:text-gray-600"
                title="Bezárás"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <FolderComments 
              folderId={selectedFolderData.id}
              folderName={selectedFolderData.name}
              folderPath={selectedFolderData.displayPath}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 